import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import {
  ArrowLeft, Wrench, ShieldCheck, ClipboardList, AlertTriangle, DollarSign,
  Building2, Bell, History, CheckCircle2,
} from "lucide-react";

const aud = (v) => `$${Number(v || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-4 border-t-4" style={{ borderTopColor: accent }} data-testid={`amachine-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
      <p className="font-mono text-3xl" style={{ color: accent }}>{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{label}</p>
    </div>
  );
}

const statusBadge = { overdue: "badge-overdue", upcoming: "badge-upcoming", ok: "badge-healthy" };

export default function AdminMachineDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/machines/${id}`).then(({ data }) => setD(data)).catch((e) => setErr(e.response?.data?.detail || "Could not load machine"));
  }, [id]);

  if (err) return <div className="p-5 md:p-10" data-testid="admin-machine-page"><p className="font-body text-[#FF3B30]">{err}</p></div>;
  if (!d) return <div className="p-5 md:p-10" data-testid="admin-machine-page"><p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p></div>;

  const { machine: m, fleet, stats } = d;

  return (
    <div className="p-5 md:p-10 pb-24 max-w-4xl" data-testid="admin-machine-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4" data-testid="amachine-back"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div>
          <h1 className="font-head font-900 uppercase tracking-tight text-3xl">{m.fleet_number ? `${m.fleet_number} · ` : ""}{m.make} {m.model}</h1>
          <p className="font-mono text-xs text-[#94A3B8]">{(m.type || "other").toUpperCase()}{m.rego_number ? ` · ${m.rego_number}` : ""}{m.serial ? ` · SN ${m.serial}` : ""} · {m.running_hours || 0}h · {m.odometer_km || 0}km</p>
          <button onClick={() => nav(`/admin/fleets/${fleet.id}`)} className="font-mono text-xs text-[#FFB800] flex items-center gap-1.5 mt-1" data-testid="amachine-fleet-link"><Building2 className="w-3.5 h-3.5" /> {fleet.name}</button>
        </div>
        {m.flagged && <span className="badge badge-overdue flex items-center gap-1" data-testid="amachine-flagged"><AlertTriangle className="w-3 h-3" /> Flagged</span>}
      </div>

      {m.flagged && m.flag_reason && (
        <div className="card p-4 mb-4 border-l-4 border-[#FF3B30]" data-testid="amachine-flag-reason">
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#FF3B30] mb-1">Current fault</p>
          <p className="font-body text-sm text-white/90">{m.flag_reason}</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat icon={ClipboardList} label="Work Orders" value={stats.work_orders} accent="#FFB800" />
        <Stat icon={ShieldCheck} label="Pre-Starts" value={stats.prestarts} accent="#22C55E" />
        <Stat icon={AlertTriangle} label="PS Faults" value={stats.prestart_fails} accent="#FF3B30" />
        <Stat icon={Wrench} label="Services" value={stats.services} accent="#3B82F6" />
      </div>

      <div className="card mb-4" data-testid="amachine-reminders">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Bell className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Reminders ({d.reminders.length})</h2></div>
        {d.reminders.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No reminders.</p> : (
          <div className="divide-y divide-white/10">
            {d.reminders.map((r) => (
              <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-body font-700 truncate">{r.title}</p><p className="font-mono text-[11px] text-[#94A3B8]">due {r.due_date ? fmtDate(r.due_date) : "—"}</p></div>
                <span className={`badge ${statusBadge[r.status] || "badge-healthy"} shrink-0`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mb-4" data-testid="amachine-workorders">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><ClipboardList className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Work Orders ({d.work_orders.length})</h2></div>
        {d.work_orders.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No work orders.</p> : (
          <div className="divide-y divide-white/10">
            {d.work_orders.map((w) => (
              <div key={w.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-body font-700 truncate flex items-center gap-2">{w.title}{w.auto_prestart && <span className="badge badge-overdue">auto</span>}</p><p className="font-mono text-[11px] text-[#94A3B8]">{w.assignee} · {w.priority} · {fmtDate(w.created_at)}</p></div>
                <span className={`badge ${w.status === "done" ? "badge-healthy" : "badge-overdue"} shrink-0`}>{w.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mb-4" data-testid="amachine-prestarts">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><ShieldCheck className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Pre-Start Checks ({d.prestarts.length})</h2></div>
        {d.prestarts.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No pre-starts logged.</p> : (
          <div className="divide-y divide-white/10">
            {d.prestarts.map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {p.overall === "fail" ? <AlertTriangle className="w-4 h-4 text-[#FF3B30] shrink-0" /> : <ShieldCheck className="w-4 h-4 text-[#22C55E] shrink-0" />}
                  <div className="min-w-0"><p className="font-body truncate">{p.operator_name}</p>{p.overall === "fail" && (p.failed_items || []).length > 0 && <p className="font-mono text-[11px] text-[#FF3B30]">{p.failed_items.join(", ")}</p>}</div>
                </div>
                <span className="font-mono text-[11px] text-[#94A3B8] shrink-0">{fmtDate(p.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card mb-4" data-testid="amachine-services">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><Wrench className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Services ({d.services.length})</h2></div>
        {d.services.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No services logged.</p> : (
          <div className="divide-y divide-white/10">
            {d.services.map((s) => (
              <div key={s.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-body font-700 truncate">{s.service_type}</p>{s.parts_replaced && <p className="font-mono text-[11px] text-[#94A3B8] truncate">{s.parts_replaced}</p>}</div>
                <div className="text-right shrink-0"><p className="font-mono text-sm text-[#22C55E] flex items-center gap-1 justify-end"><DollarSign className="w-3 h-3" />{aud(s.cost)}</p><p className="font-mono text-[11px] text-[#94A3B8]">{fmtDate(s.service_date)}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" data-testid="amachine-defectlog">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10"><History className="w-5 h-5 text-[#FFB800]" /><h2 className="font-head font-700 text-lg uppercase tracking-wide">Defect Log ({d.flag_events.length})</h2></div>
        {d.flag_events.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No defect events.</p> : (
          <div className="divide-y divide-white/10">
            {d.flag_events.map((f) => (
              <div key={f.id} className="px-5 py-3 flex items-start gap-3">
                {f.event === "flagged" ? <AlertTriangle className="w-4 h-4 text-[#FF3B30] shrink-0 mt-0.5" /> : <CheckCircle2 className="w-4 h-4 text-[#22C55E] shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <p className="font-body font-700 uppercase text-sm">{f.event === "flagged" ? "Flagged" : "Resolved"}{f.reason ? ` — ${f.reason}` : ""}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8]">by {f.actor}{f.source ? ` · ${f.source}` : ""} · {fmtDate(f.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
